import type {
	AISetting,
	Update as AutomationActionUpdate,
} from '@repo/api-codegen';
import { FILTER_OPTIONS_DIVIDER } from '@repo/common/components/Filter/constants';
import type { SelectablePropertyItem } from '@repo/common/components/ItemIcon/types';
import { fuzzySearchFilter } from '@repo/common/utils/fuse';
import { uuidv4 } from 'lib0/random';
import { debounce, uniqBy } from 'lodash-es';
import { makeAutoObservable, ObservableMap, reaction, runInAction } from 'mobx';
import { EDIT_ACTION_VALUE_ITEMS } from './components/AutomationEdit/constants';
import { automationActionTypeOptions } from './constants';
import {
	AutomationEditOption,
	AutomationEditType,
	type AutomationEdit,
	type EditItem,
	type EditItemFunction,
} from './types';
import {
	getEditOption,
	getValueAsArray,
	requiresEditActionValue,
} from './utils';

export interface AutomationEditAction extends Partial<AutomationEdit> {
	searchKey: string;
	type: AutomationEditType;
	value?: string | boolean | string[];
	display?: string | null;
	isAiEditAction?: boolean | null;
}

export interface EditActionAiSetting extends AISetting {
	consumed: boolean;
}

class EditActionStore {
	editBlockStore: EditBlockStore;
	editAction: AutomationEditAction;
	isChangingValue: boolean = false;
	selectedItems: EditItem[] = [];
	unselectedItems: EditItem[] = [];
	items: EditItem[] = [];
	loading: boolean = false;
	error: boolean = false;
	getItems?: EditItemFunction['getItems'];
	getItemsById?: EditItemFunction['getItemsById'];
	searchTerm: string = '';
	debouncedSearchTerm: string = '';
	stringValue: string = '';

	constructor(
		editBlockStore: EditBlockStore,
		editAction: AutomationEditAction
	) {
		makeAutoObservable(this, {}, { autoBind: true });

		this.editBlockStore = editBlockStore;
		this.editAction = editAction;
		this.setGetItemsAndGetItemsById();
		this.stringValue =
			typeof editAction.value === 'string' ? editAction.value : '';

		this.setDebouncedSearchTerm = debounce((searchTerm: string) => {
			runInAction(() => {
				this.debouncedSearchTerm = searchTerm;
			});
		}, 300);

		reaction(
			() => this.debouncedSearchTerm,
			() => {
				this.getEditItems();
			}
		);
		this.getEditItems();
	}

	setDebouncedSearchTerm: (searchTerm: string) => void;

	setValue(value: string | boolean) {
		this.editAction.value = value;
		this.isChangingValue = false;
		this.editBlockStore.updateEditSettings();
	}

	setItems() {
		const values = getValueAsArray({
			value: this.editAction.value ?? '',
			label: this.editAction.display ?? '',
		});

		const { selectedItems, unselectedItems } = this.items.reduce<{
			selectedItems: EditItem[];
			unselectedItems: EditItem[];
		}>(
			(acc, item) => {
				if (!Array.isArray(item.value) && values.includes(item.value)) {
					acc.selectedItems.push(item);
				} else {
					acc.unselectedItems.push(item);
				}
				return acc;
			},
			{ selectedItems: [], unselectedItems: [] }
		);

		this.selectedItems = selectedItems;
		this.unselectedItems = unselectedItems;
	}

	setGetItemsAndGetItemsById() {
		const editOption = getEditOption(this.editAction.type);

		if (
			![
				AutomationEditOption.DESCRIPTION,
				AutomationEditOption.DOCUMENTATION,
			].includes(editOption)
		) {
			this.getItems = EDIT_ACTION_VALUE_ITEMS[editOption].getItems;
			this.getItemsById = EDIT_ACTION_VALUE_ITEMS[editOption].getItemsById;
		}
	}

	setDisplay(display: string) {
		this.editAction.display = display;
	}

	reset() {
		this.editAction.value = undefined;
		this.editAction.display = undefined;
		this.searchTerm = '';
		this.debouncedSearchTerm = '';
		this.items = [];
		this.loading = false;
		this.error = false;
		this.selectedItems = [];
		this.stringValue = '';

		this.getEditItems();
	}

	getEditAction(): AutomationEditAction {
		return this.editAction;
	}

	getValueLabel() {
		const value = {
			value: this.editAction.value ?? '',
			label: this.editAction.display ?? '',
		};
		const selectedValues: EditItem[] = [];

		if (this.editAction.isAiEditAction) {
			return this.editAction.display ?? '(no selection)';
		}

		if (!this.getItems && !this.getItemsById) {
			return this.editAction.display ?? '(no selection)';
		}

		if (this.error) {
			return 'Error loading filter';
		}
		if (this.loading) {
			return 'Loading';
		}

		const values = getValueAsArray(value);

		selectedValues.push(
			...this.items.filter(
				(item) => !Array.isArray(item.value) && values.includes(item.value)
			)
		);

		if (selectedValues.length === 0) {
			return null;
		}

		if (selectedValues.length > 1) {
			return `${selectedValues.length} items`;
		}

		return selectedValues.map((item) => item.label).join(', ');
	}

	setSearchTerm(searchTerm: string) {
		this.searchTerm = searchTerm;
		this.setDebouncedSearchTerm(searchTerm);
	}

	setStringValue(stringValue: string) {
		this.stringValue = stringValue;
	}

	async getEditItems() {
		const value = {
			value: this.editAction.value ?? '',
			label: this.editAction.display ?? '',
		};

		if (!this.getItems && !this.getItemsById) {
			return;
		}

		if (this.getItems && typeof this.getItems !== 'function') {
			const filteredItems = this.getItems
				.filter(
					(item) =>
						item !== FILTER_OPTIONS_DIVIDER &&
						(item.label
							.toLowerCase()
							.includes(this.debouncedSearchTerm.toLocaleLowerCase()) ||
							item.value
								?.toString()
								?.toLowerCase()
								?.includes(this.debouncedSearchTerm.toLocaleLowerCase()))
				)
				.filter((item) => item !== FILTER_OPTIONS_DIVIDER) as EditItem[];

			this.items = filteredItems;
			this.loading = false;
			this.error = false;
		} else if (this.getItems && typeof this.getItems === 'function') {
			this.loading = true;

			const getAllItems = async () => {
				if (typeof this.getItems !== 'function') {
					return [];
				}

				let allItems = await this.getItems(1, this.debouncedSearchTerm);

				const selectedValues = value ? getValueAsArray(value) : [];

				if (
					selectedValues?.length > 0 &&
					selectedValues.some(
						(selectedValue) =>
							!allItems.find(
								(item) =>
									typeof item !== 'string' && item.value === selectedValue
							)
					)
				) {
					const itemsById = await Promise.resolve(
						this.getItemsById
							? this.getItemsById(selectedValues as string[])
							: []
					);
					allItems = uniqBy(
						[...itemsById, ...allItems],
						(item) => typeof item !== 'string' && item.value
					);
				}

				return allItems as EditItem[];
			};

			getAllItems()
				.then((items) => {
					this.items = items;
					this.loading = false;
					this.error = false;
				})
				.catch(() => {
					this.error = true;
				})
				.finally(() => {
					this.loading = false;
					this.setItems();
				});
		}
	}
}

export class EditBlockStore {
	editSettings: AutomationActionUpdate[];
	overrideExistingProperties: boolean = false;
	editActions: ObservableMap<string, EditActionStore> = new ObservableMap();
	actionOptions: SelectablePropertyItem[] = [];
	searchTerm: string = '';
	aiSettings: EditActionAiSetting[];

	constructor(
		initialEditSettings: AutomationActionUpdate[],
		initialShouldOverwriteMetadata: boolean,
		aiSettings: AISetting[]
	) {
		makeAutoObservable(this, {}, { autoBind: true });

		this.editSettings = initialEditSettings;
		this.overrideExistingProperties = initialShouldOverwriteMetadata;
		this.aiSettings = aiSettings.map((aiSetting) => ({
			...aiSetting,
			consumed: false,
		}));
		this.setEditAction();
		this.autoApplyAiBindings();
	}

	setOverrideExistingProperties(overrideExistingProperties: boolean) {
		this.overrideExistingProperties = overrideExistingProperties;
	}

	setSearchTerm(searchTerm: string) {
		this.searchTerm = searchTerm;
	}

	setEditAction() {
		for (const editSetting of this.editSettings) {
			const editActionStore = this.editActions.get(editSetting.key);
			if (!editActionStore) {
				this.editActions.set(
					editSetting.key,
					new EditActionStore(this, {
						searchKey: editSetting.key,
						type: editSetting.type as AutomationEditType,
						value: editSetting.value,
						display: editSetting.display,
						isAiEditAction: editSetting.is_ai_edit_action ?? null,
					})
				);
			}
			const foundAiSetting = this.aiSettings.find(
				(aiSetting) => aiSetting.id === editSetting.key
			);
			if (foundAiSetting) {
				foundAiSetting.consumed = true;
			}
		}
	}

	getEditActionOptions(searchTerm: string) {
		this.actionOptions = fuzzySearchFilter(
			searchTerm || '',
			automationActionTypeOptions,
			['label']
		).filter((f) => !f.hidden);
		return this.actionOptions;
	}

	onAddEditAction = (type: AutomationEditType): AutomationEditAction => {
		const searchKey = uuidv4();

		this.editActions.set(
			searchKey,
			new EditActionStore(this, {
				searchKey,
				type,
			})
		);

		const editActionStore = this.editActions.get(searchKey);

		if (!editActionStore) {
			throw new Error('Edit action not found');
		}

		if (!requiresEditActionValue(type)) {
			editActionStore.setValue('');
		}

		return editActionStore.getEditAction();
	};

	addAiBindings = (aiSetting: EditActionAiSetting) => {
		// Create edit actions for each selected bind with AI marker
		const idBase = aiSetting.id;

		const createAction = (
			keySuffix: string,
			type: AutomationEditType,
			fieldName: string,
			value: string
		) => {
			const key = `${idBase}:${keySuffix}`;
			this.editActions.set(
				key,
				new EditActionStore(this, {
					searchKey: key,
					type,
					value,
					display: `Apply AI ${fieldName}`,
					isAiEditAction: true,
				})
			);
		};

		if (aiSetting.bind_description) {
			createAction(
				'description',
				AutomationEditType.DESCRIPTION_SET,
				'description',
				'AI-generated description'
			);
		}

		if (aiSetting.bind_definition) {
			createAction(
				'definition',
				AutomationEditType.DEFINITION_SET,
				'documentation',
				'AI-generated documentation'
			);
		}

		if (aiSetting.bind_pii) {
			createAction('pii', AutomationEditType.PII_TRUE, 'PII', 'true');
		}

		if (aiSetting.bind_tags) {
			createAction(
				'tags',
				AutomationEditType.TAGS_REPLACE,
				'tags',
				'AI-generated tags'
			);
		}

		if (aiSetting.bind_owners) {
			createAction(
				'owners',
				AutomationEditType.OWNERS_REPLACE,
				'owners',
				'AI-generated owners'
			);
		}

		const found = this.aiSettings.find((s) => s.id === aiSetting.id);
		if (found) {
			found.consumed = true;
		}

		this.updateEditSettings();
	};

	autoApplyAiBindings = () => {
		// Automatically apply AI settings that have bindings configured
		this.aiSettings.forEach((aiSetting) => {
			const hasAnyBind = Boolean(
				aiSetting.bind_pii ||
					aiSetting.bind_tags ||
					aiSetting.bind_owners ||
					aiSetting.bind_description ||
					aiSetting.bind_definition
			);

			// If this AI setting has bindings and isn't already consumed, auto-apply it
			if (hasAnyBind && !aiSetting.consumed) {
				this.addAiBindings(aiSetting);
			}
		});
	};

	updateAiSettings = (newAiSettings: AISetting[]) => {
		// Remove existing AI-generated edit actions
		const keysToDelete: string[] = [];
		this.editActions.forEach((editActionStore, key) => {
			if (editActionStore.editAction.isAiEditAction) {
				keysToDelete.push(key);
			}
		});
		keysToDelete.forEach((key) => this.editActions.delete(key));

		// Update AI settings
		this.aiSettings = newAiSettings.map((aiSetting) => ({
			...aiSetting,
			consumed: false,
		}));

		// Reapply bindings
		this.autoApplyAiBindings();
		this.updateEditSettings();
	};

	deleteEditAction = (key: string) => {
		const editActionStore = this.editActions.get(key);
		if (editActionStore?.editAction.isAiEditAction) {
			const foundAiSetting = this.aiSettings.find(
				(aiSetting) => aiSetting.id === key
			);
			if (foundAiSetting) {
				foundAiSetting.consumed = false;
			}
		}
		this.editActions.delete(key);
		this.updateEditSettings();
	};

	updateEditSettings() {
		const newSettings: AutomationActionUpdate[] = [];

		this.editActions.forEach((editActionStore) => {
			const value =
				typeof editActionStore?.editAction.value === 'boolean'
					? ''
					: editActionStore?.editAction.value;
			if (editActionStore) {
				newSettings.push({
					key: editActionStore.editAction.searchKey,
					type: editActionStore.editAction.type,
					value: value ?? '',
					display: editActionStore.editAction.display ?? '',
					is_ai_edit_action: editActionStore.editAction.isAiEditAction ?? null,
				});
			}
		});

		this.editSettings = newSettings;
	}
}
